import type { AxiosError } from 'axios'
import { AxiosResponse, InternalAxiosRequestConfig } from './types'
import { ElMessage, ElMessageBox } from 'element-plus'
import qs from 'qs'
import { SUCCESS_CODE, TRANSFORM_REQUEST_DATA } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { objToFormData } from '@/utils'

// 单点登录被踢的全局标志，避免一次性多个请求同时返回 402 时弹多个弹框
let ssoKickedOut = false

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  if (
    config.method === 'post' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    config.data = qs.stringify(config.data)
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === 'post' &&
    config.headers['Content-Type'] === 'multipart/form-data' &&
    !(config.data instanceof FormData)
  ) {
    config.data = objToFormData(config.data)
  }
  if (config.method === 'get' && config.params) {
    let url = config.url as string
    url += '?'
    const keys = Object.keys(config.params)
    for (const key of keys) {
      if (config.params[key] !== void 0 && config.params[key] !== null) {
        url += `${key}=${encodeURIComponent(config.params[key])}&`
      }
    }
    url = url.substring(0, url.length - 1)
    config.params = {}
    config.url = url
  }
  return config
}

/** HTTP 错误（如 Laravel 422）：拼出可读文案，避免只显示「Request failed with status code 422」 */
export const getAxiosErrorDisplayMessage = (error: AxiosError): string => {
  const d = error.response?.data as
    { message?: unknown; msg?: unknown; errors?: Record<string, string[] | string> } | undefined
  if (d && typeof d === 'object') {
    const errs = d.errors
    if (errs && typeof errs === 'object') {
      const lines: string[] = []
      for (const val of Object.values(errs)) {
        if (Array.isArray(val)) {
          for (const item of val) {
            if (item != null && String(item).trim()) {
              lines.push(String(item).trim())
            }
          }
        } else if (typeof val === 'string' && val.trim()) {
          lines.push(val.trim())
        }
      }
      if (lines.length > 0) {
        return lines.join('；')
      }
    }
    const msg = d.message ?? d.msg
    if (typeof msg === 'string' && msg.trim()) {
      return msg.trim()
    }
  }
  const m = error.message?.trim()
  if (m) return m
  return '请求失败'
}

const defaultResponseInterceptors = (response: AxiosResponse) => {
  if (response?.config?.responseType === 'blob') {
    // 如果是文件流，直接过
    return response
  } else {
    const rawCode = response.data?.code
    // 仅当 code 是非空字符串时才数字化兼容（避免 null/""/false 被 Number(...) 误判为 0）
    const normalizedCode =
      typeof rawCode === 'string' && rawCode.trim() !== '' ? Number(rawCode) : rawCode
    if (normalizedCode === SUCCESS_CODE || normalizedCode === 200) {
      return response.data
    }
    // 单点登录：账号已在其他设备登录
    if (response?.data?.code === 402) {
      if (!ssoKickedOut) {
        ssoKickedOut = true
        ElMessageBox.alert(
          response?.data?.message || '您的账号已在其他设备登录，请重新登录',
          '提示',
          {
            type: 'warning',
            showClose: false,
            confirmButtonText: '重新登录'
          }
        )
          .catch(() => {})
          .finally(() => {
            const userStore = useUserStoreWithOut()
            userStore.logout()
            // logout 内部会清 token 并跳登录；下次登录前重置一下标志
            setTimeout(() => {
              ssoKickedOut = false
            }, 1000)
          })
      }
      return response.data
    }
    if (response?.data?.code === 401) {
      ElMessage.error('登录已过期，请重新登录')
      const userStore = useUserStoreWithOut()
      userStore.logout()
    } else if (response?.config?.silentGlobalMessage === true) {
      return response.data
    } else {
      ElMessage.error(response?.data?.message || '请求失败')
    }
  }
}

export { defaultResponseInterceptors, defaultRequestInterceptors }
